import { useCallback, useEffect, useState } from 'react';
import { ArrowLeftOutlined, UploadOutlined } from '@ant-design/icons';
import { css, Global } from '@emotion/react';
import {
    Button,
    Col,
    Form,
    Input,
    Row,
    Select,
    Typography,
    Upload
} from 'antd';
import CustomLabel from '@/components/custom-label';
import {
    getListFood,
    getListProduct,
    createProduct,
    watchGroupListFood,
    watchProductMessage,
    watchProductLoading,
    resetMessages,
} from '@/redux/slices/product.slice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useQuery } from '@/hooks/query.hook';
import { PageURLs } from '@/utils/navigate';
import { setMessages } from '@/redux/slices/app.slice';
import { formatProductTag } from '@/utils/validator';

const ProductManagementCreateForm = () => {
    const [form] = Form.useForm();
    const dispatch = useAppDispatch();
    const [fileThumbnail, setFileThumbnail] = useState<any[]>([]);
    const [fileLogo, setFileLogo] = useState<any[]>([]);
    const groupListFood = useAppSelector(watchGroupListFood);
    const messageInfo = useAppSelector(watchProductMessage);
    const loading = useAppSelector(watchProductLoading);
    const { push } = useQuery();

    useEffect(() => {
        dispatch(getListFood({ limit: 9999 }));
    }, [dispatch]);

    const beforeUploadThumbnail = (file: File) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            setFileThumbnail([{ url: reader.result }]);
        };
        return false;
    };

    const handleRemoveThumbnail = () => {
        setFileThumbnail([]);
        form.setFieldValue('thumbnail', undefined);
        form.validateFields(['thumbnail']);
    };

    const beforeUploadLogo = (file: File) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            setFileLogo([{ url: reader.result }]);
        };
        return false;
    };

    const handleRemoveLogo = () => {
        setFileLogo([]);
        form.setFieldValue('logo', undefined);
        form.validateFields(['logo']);
    };

    const foodGroupOptions = groupListFood.map((item) => ({
        value: item.id,
        label: item.name + ' - ' + item.id
    }));

    const onFinish = async (values: any) => {
        let body: any = {
            NAME: values.name,
            ID_FOOD: values.idFood,
            TAG: values.tag,
            SHORT_DESC: values.shortDesc
        };
        if (fileThumbnail.length > 0 && fileThumbnail[0].url) {
            const cleanThumbnailUrl = (fileThumbnail[0].url as string)?.split('?')[0] ?? fileThumbnail[0].url;
            body = {
                ...body,
                THUMBNAIL: cleanThumbnailUrl
            };
        }
        if (fileLogo.length > 0 && fileLogo[0].url) {
            const cleanLogoUrl = (fileLogo[0].url as string)?.split('?')[0] ?? fileLogo[0].url;
            body = {
                ...body,
                LOGO: cleanLogoUrl
            };
        }
        const result = await dispatch(createProduct(body));
        if (createProduct.rejected.match(result)) {
          return;
        }
        await dispatch(getListProduct({}));
        push(PageURLs.ofProductManagement());
    };

    useEffect(() => {
        if (messageInfo) {
            dispatch(
                setMessages([
                    {
                        type: messageInfo.type,
                        message: messageInfo.message,
                        description: messageInfo.description
                    }
                ])
            );

            dispatch(resetMessages());
        }
    }, [dispatch, messageInfo]);

    const handleGoBack = useCallback(() => {
        push(PageURLs.ofProductManagement());
    }, [push]);

    return (
        <>
          <Global
            styles={css`
              .ant-form-item-label span {
                  font-weight: bold !important;
              }
            `}
          />
          <Form form={form} validateTrigger='onSubmit' css={rootStyles} onFinish={onFinish}>
              <Row css={boxStyles} align='middle' gutter={16}>
                  <Button
                      type='text'
                      icon={<ArrowLeftOutlined />}
                      onClick={handleGoBack}
                  ></Button>
                  <Typography.Title level={5} style={{ marginBottom: 0 }}>
                      Tự nhập từng sản phẩm
                  </Typography.Title>
              </Row>
  
              <Row css={boxStyles} align='top' gutter={16}>
                  <Col span={24} css={fieldStyles}>
                      <Form.Item
                          name='name'
                          label={CustomLabel({ text: 'Tên sản phẩm', isRequired: true })}
                          labelCol={{ span: 4 }}
                          rules={[{ required: true, message: 'Vui lòng điền thông tin!' }]}
                      >
                          <Input placeholder='Nhập tên sản phẩm' />
                      </Form.Item>
                  </Col>
  
                  <Col span={24} css={fieldStyles}>
                      <Form.Item
                          name='foodName'
                          label={CustomLabel({ text: 'Thực phẩm tương ứng', isRequired: true })}
                          labelCol={{ span: 4 }}
                          rules={[{ required: true, message: 'Vui lòng điền thông tin!' }]}
                      >
                          <Select
                              placeholder='Chọn thực phẩm tương ứng với sản phẩm'
                              options={foodGroupOptions}
                              allowClear
                              onChange={(value) => {
                                  form.setFieldsValue({ idFood: value });
                              }}
                          />
                      </Form.Item>
                  </Col>
  
                  <Col span={24} css={fieldStyles}>
                      <Form.Item
                          name='idFood'
                          label={CustomLabel({ text: 'ID thực phẩm tương ứng' })}
                          labelCol={{ span: 4 }}
  
                      >
                          <Input
                              placeholder='ID thực phẩm tương ứng với sản phẩm'
                              readOnly
                          />
                      </Form.Item>
                  </Col>
  
                  <Col span={24} css={fieldStyles}>
                      <Form.Item
                          name='tag'
                          label={CustomLabel({ text: 'Từ khoá nhãn sản phẩm' })}
                          labelCol={{ span: 4 }}
                      >
                          <Input 
                              placeholder='Ví dụ: HAT_NEM' 
                              onChange={(e) => {
                                  const formattedValue = formatProductTag(e.target.value);
                                  form.setFieldsValue({ tag: formattedValue });
                              }}
                          />
                      </Form.Item>
                  </Col>
  
                  <Col span={24} css={fieldStyles}>
                      <Form.Item
                          name='thumbnail'
                          label={CustomLabel({ text: 'Ảnh sản phẩm', isRequired: true })}
                          labelCol={{ span: 4 }}
                          rules={[
                              {
                                  validator: (_,) => {
                                      if (fileThumbnail && fileThumbnail.length > 0) {
                                          return Promise.resolve();
                                      }
                                      return Promise.reject(new Error('Vui lòng điền thông tin!'));
                                  },
                              },
                          ]}
                      >
                          <Upload
                              beforeUpload={beforeUploadThumbnail}
                              listType='picture'
                              accept='.png, .jpeg, .jpg'
                              onRemove={handleRemoveThumbnail}
                              maxCount={1}
                              multiple={false}
                          >
                              <Button icon={<UploadOutlined />}>
                                  Chọn để tải lên hình ảnh
                              </Button>
                          </Upload>
                      </Form.Item>
                  </Col>
  
                  <Col span={24} css={fieldStyles}>
                      <Form.Item
                          name='logo'
                          label={CustomLabel({ text: 'Logo nhãn sản phẩm' })}
                          labelCol={{ span: 4 }}
                      >
                          <Upload
                              beforeUpload={beforeUploadLogo}
                              listType='picture'
                              accept='.png, .jpeg, .jpg'
                              onRemove={handleRemoveLogo}
                              maxCount={1}
                              multiple={false}
                          >
                              <Button icon={<UploadOutlined />}>
                                  Chọn để tải lên hình ảnh
                              </Button>
                          </Upload>
                      </Form.Item>
                  </Col>
  
                  <Col span={24} css={fieldStyles}>
                      <Form.Item
                          name='shortDesc'
                          label={CustomLabel({ text: 'Mô tả ngắn', isRequired: true })}
                          labelCol={{ span: 4 }}
                          rules={[{ required: true, message: 'Vui lòng điền thông tin!' }]}
                      >
                        <Input.TextArea
                          placeholder='Viết mô tả sản phẩm'
                          autoSize={{ minRows: 1, maxRows: 4 }}
                          maxLength={5000}
                        />
                      </Form.Item>
                  </Col>
              </Row>
  
              <Row css={groupButtonStyles} align='top' gutter={16}>
                  <Button type='default' size='large' onClick={handleGoBack}>
                      Hủy
                  </Button>
  
                  <Form.Item noStyle>
                      <Button type='primary' size='large' htmlType='submit' loading={loading}>
                          Lưu
                      </Button>
                  </Form.Item>
              </Row>
          </Form>
        </>
    );
};
export default ProductManagementCreateForm;

const rootStyles = css`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.4rem 3.4rem;
  min-height: calc(100vh - var(--header-height));
  background: var(--gray-soft-color);
`;

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
`;

const fieldStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;

  .ant-form-item {
    width: 100%;
    margin-bottom: 6px;

    .ant-form-item-row {
      display: flex;
      justify-content: space-between;
    }

    .ant-form-item-label {
      text-align: start !important;

      label {
        white-space: normal;
        height: auto;
        line-height: 1.5;
        
        &::after,
        &::before {
          display: none !important;
        }
      }
    }
  }

  input {
    flex: 1;
  }
`;

const groupButtonStyles = css`
  padding: 1rem;
  background: var(--white-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }

  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  gap: 1.6rem;

  button {
    padding: 1rem 10rem;
  }
`;